import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Trash2, Plus, Save, Edit2, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const EMPTY = { name: '', slug: '', description: '', icon: '🥜', order: 0, active: true };

export const ForumRoomsAdmin = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = React.useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      // The list endpoint requires a Nuts member token, so admin shouldn't be able to call it.
      // For admin we expose a sibling pattern: just use the same endpoint with admin token —
      // but the backend currently requires nuts_member. To keep the admin UI working without
      // a separate endpoint, we fall back to listing via Mongo through a small admin endpoint
      // we'll add. For now: try the public list (it requires nuts_member) — if it fails,
      // we use a graceful fallback by hitting the create form with empty state.
      const r = await axios.get(`${API_URL}/api/forum/admin/rooms`, { headers: authHeaders });
      setRooms(r.data || []);
    } catch (e) {
      toast.error('Error cargando salas');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = React.useCallback((room) => {
    setEditing(room.id);
    setForm({
      name: room.name,
      slug: room.slug,
      description: room.description || '',
      icon: room.icon || '🥜',
      order: room.order || 0,
      active: room.active !== false,
    });
  }, []);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Nombre obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug.trim() || slugify(form.name) };
      if (editing) {
        await axios.put(`${API_URL}/api/forum/rooms/${editing}`, payload, { headers: authHeaders });
        toast.success('Sala actualizada');
      } else {
        await axios.post(`${API_URL}/api/forum/rooms`, payload, { headers: authHeaders });
        toast.success('Sala creada');
      }
      setEditing(null);
      setForm(EMPTY);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando sala');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const remove = async (room) => {
    if (
      !window.confirm(`¿Eliminar la sala "${room.name}" y TODOS sus hilos y comentarios?`)
    )
      return;
    try {
      await axios.delete(`${API_URL}/api/forum/rooms/${room.id}`, { headers: authHeaders });
      toast.success('Sala eliminada');
      await load();
    } catch (e) {
      toast.error('Error eliminando sala');
    }
  };

  const toggleActive = async (room) => {
    try {
      await axios.put(
        `${API_URL}/api/forum/rooms/${room.id}`,
        { ...room, active: !room.active },
        { headers: authHeaders }
      );
      await load();
    } catch (e) {
      toast.error('Error actualizando estado');
    }
  };

  if (loading) return <div className="py-10 text-center text-slate-500">Cargando salas...</div>;

  return (
    <div className="space-y-6" data-testid="forum-rooms-admin">
      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {editing ? '✏️ Editar sala' : '➕ Crear nueva sala'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Nombre *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Reserves & Tee Times"
                data-testid="forum-room-name"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Slug (URL)</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder={form.name ? slugify(form.name) : 'auto-generado del nombre'}
                data-testid="forum-room-slug"
              />
              <p className="text-xs text-slate-500 mt-1">/nuts/forum/r/{form.slug || slugify(form.name) || 'slug'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Icono (emoji)</Label>
              <Input
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="🥜"
                data-testid="forum-room-icon"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Orden</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || '0', 10) })}
                data-testid="forum-room-order"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Descripción</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Una frase corta que describe la sala..."
              rows={2}
              data-testid="forum-room-description"
            />
          </div>
          <div className="flex items-center justify-between pt-2">
            {editing && (
              <Button variant="outline" size="sm" onClick={cancelEdit}>
                Cancelar edición
              </Button>
            )}
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 ml-auto"
              data-testid="forum-room-save"
            >
              {editing ? (
                <><Save className="mr-2 h-4 w-4" /> {saving ? 'Guardando...' : 'Guardar cambios'}</>
              ) : (
                <><Plus className="mr-2 h-4 w-4" /> {saving ? 'Creando...' : 'Crear sala'}</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing rooms */}
      <div>
        <h3 className="text-base font-semibold mb-3">
          Salas existentes ({rooms.length})
        </h3>
        {rooms.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-slate-500">
              Aún no hay salas. Crea la primera arriba.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {rooms.map((room) => (
              <Card key={room.id} className={!room.active ? 'opacity-60' : ''}>
                <CardContent className="py-4 flex items-center gap-4">
                  <span className="text-3xl flex-shrink-0">{room.icon || '💬'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-base font-semibold truncate">{room.name}</h4>
                      <span className="text-xs text-slate-400">/{room.slug}</span>
                      {!room.active && (
                        <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                          Oculta
                        </span>
                      )}
                    </div>
                    {room.description && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{room.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActive(room)}
                      data-testid={`forum-room-toggle-${room.slug}`}
                    >
                      {room.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(room)}
                      data-testid={`forum-room-edit-${room.slug}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(room)}
                      data-testid={`forum-room-delete-${room.slug}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ForumRoomsAdmin;
